<template>
	<div class="EntityForm">
		<div class="flex row-left-center mb-2">
			<div class="flex-1">
				<WText tag="label" typo="label" :size="14">
					{{ $t("entities.creation.labels.key") }}
				</WText>
				<WFieldError :validator="$v.key" :label="$t('entities.validations.key')" #default="{ errorMessage, listeners }">
					<WInput v-model="key" name="key" :error="errorMessage" :placeholder="$t('entities.creation.placeholder.key')" type="text" :disabled="locked" v-on="listeners" />
				</WFieldError>
			</div>
			<div class="flex-1 ml-1">
				<WText tag="label" typo="label" :size="14">
					{{ $t("entities.creation.labels.name") }}
				</WText>
				<WFieldError :validator="$v.name" :label="$t('entities.validations.name')" #default="{ errorMessage, listeners }">
					<WInput
						v-model="name"
						:error="errorMessage"
						name="name"
						:placeholder="$t('entities.creation.placeholder.name')"
						type="text"
						:disabled="isLoading"
						v-on="listeners"
					/>
				</WFieldError>
			</div>
		</div>
		<div class="flex row-left-center mb-2">
			<div class="flex-1">
				<WText tag="label" typo="label" :size="14">
					{{ $t("entities.creation.labels.description") }}
				</WText>
				<WInput v-model="description" name="description" type="text" :placeholder="$t('entities.creation.placeholder.description')" :disabled="isLoading" />
			</div>
			<div class="flex-1 ml-1">
				<WText tag="label" typo="label" :size="14">
					{{ $t("entities.creation.labels.group") }}
				</WText>
				<WSelect
					v-model="groupKey"
					name="groupKey"
					:placeholder="$t('entities.creation.placeholder.group')"
					:options="normalizeGroups"
					:disabled="isLoading || !(normalizeGroups || []).length"
				/>
			</div>
		</div>
		<div class="flex row-left-center mb-2">
			<div class="flex-1">
				<WText tag="label" typo="label" :size="14">
					{{ $t("entities.creation.labels.defaultExpression") }}
				</WText>
				<WCode
					v-model="defaultExpression"
					name="defaultExpression"
					:placeholder="$t('entities.creation.placeholder.defaultExpression')"
					:disabled="isLoading"
					:warningOptions="{ withPredefVariables: entityKey, undef: true }"
				/>
			</div>
		</div>

	<WText tag="label" typo="label" weight="semi" color="accent-blue" size="14" uppercase class="Title mb-1">
		{{ $t("entities.creation.labels.dataTrial") }}
	</WText>

		<div class="flex row-left-top mb-2">
			<div class="flex row-left-bottom flex-1">
				<div class="flex-1">
					<WText tag="label" typo="label" :size="14">
						{{ $t("entities.creation.labels.sampleId") }}
					</WText>
					<WInput v-model="sampleDataId" name="sampleDataId" type="number" :placeholder="$t('entities.creation.placeholder.sampleId')" :disabled="compilingExpression" />
				</div>
				<div class="ml-1">
					<WButton outline color="accent-blue" :disabled="compileDisabled" :loading="compilingExpression" @click="compileExpression">
						{{ compilingExpression ? $t("entities.creation.loading") : $t("entities.creation.calculate") }}
					</WButton>
				</div>
			</div>
			<div class="flex row-left-top flex-1 ml-2">
				<div class="flex-1">
					<WText tag="label" typo="label" :size="14">
						{{ $t("entities.creation.labels.result") }}
					</WText>
					<WText :size="18">
						{{ dataTrialResult.result || "..." }}
					</WText>
				</div>
				<div class="flex-1 ml-1">
					<WText tag="label" typo="label" :size="14">
						{{ $t("entities.creation.labels.errorType") }}
					</WText>
					<WText :size="18">
						{{ dataTrialResult.error || "..." }}
					</WText>
				</div>
			</div>
		</div>

	<WText tag="label" typo="label" weight="semi" color="accent-blue" size="14" uppercase class="Title mb-1">
		{{ $t("entities.creation.labels.validation") }}
	</WText>

		<div class="flex row-left-top mb-2">
			<div class="flex-1">
				<WText tag="label" typo="label" :size="14">
					{{ $t("entities.creation.labels.type") }}
				</WText>
				<WSelect v-model="type" name="type" :options="types" :disabled="isLoading" />

				<FadeTransition mode="out-in">
					<div class="flex row-stretch-center Item bordered mt-2" v-if="isSelect">
						<p class="WText p gray left fs14 mukta">{{ $t("entities.creation.labels.multiple") }}</p>
						<WSwitch v-model="multiple" name="autoMatch" :disabled="isLoading" color="accent-blue" />
					</div>
				</FadeTransition>

				<FadeTransition mode="out-in">
					<Dictionaries v-if="isSelect" v-model="dictionary" class="mt-2" :isLoading="isLoading" />
				</FadeTransition>
			</div>
			<div class="flex-1 ml-1">
				<Validators v-model="validators" :isLoading="isLoading" />
			</div>
		</div>

	<WText tag="label" typo="label" weight="semi" color="accent-blue" size="14" uppercase class="Title mb-1">
		{{ $t("entities.creation.labels.normalization") }}
	</WText>

		<Sources v-model="mappers" class="mb-2" :isLoading="isLoading" />

	<WText tag="label" typo="label" weight="semi" color="accent-blue" size="14" uppercase class="Title">
		{{ $t("entities.creation.labels.options") }}
	</WText>
		<!-- Opciones -->
		<div class="flex row-left-top mb-2">
			<div class="flex-1">
				<!-- Estilos -->
				<Options v-model="styles" section="styles" :isLoading="isLoading" />
			</div>
			<div class="flex-1 ml-1">
				<!-- Permisos -->
				<WText tag="label" typo="label" size="14">
					{{ $t("entities.creation.labels.permissions") }}
				</WText>
				<div class="Options">
					<div class="Item flex row-stretch-center">
						<WText color="gray" :size="14">
							{{ $t("entities.creation.labels.visible") }}
						</WText>
						<div>
							<WSwitch v-model="visible" name="visible" :disabled="isLoading" color="accent-blue" />
						</div>
					</div>
					<div class="Item flex row-stretch-center">
						<WText color="gray" :size="14">
							{{ $t("entities.creation.labels.editable") }}
						</WText>
						<div>
							<WSwitch v-model="editable" name="editable" :disabled="isLoading" color="accent-blue" />
						</div>
					</div>
				</div>
			</div>
		</div>
		<div class="flex row-left-top mb-2">
			<div class="flex-1">
				<WText tag="label" typo="label" :size="14">
					{{ $t("entities.creation.labels.priority") }}
				</WText>
				<WFieldError :validator="$v.priority" :label="$t('entities.validations.priority')" #default="{ errorMessage, listeners }">
					<WSelect v-model="priority" name="priority" :options="priorities" :disabled="isLoading || editable === false" :error="errorMessage" v-on="listeners" />
				</WFieldError>
			</div>
		</div>

		<div class="flex row-right-center">
			<WButton outline color="accent-blue" :loading="isLoading" @click="$emit('onReset')">
				{{ $t("entities.creation.reset") }}
			</WButton>
			<WButton :loading="isLoading" class="ml-2" @click="handleSubmit">
				{{ $t("entities.creation.submit") }}
			</WButton>
		</div>
	</div>
</template>

<script>
import { validationMixin } from "vuelidate";
import { required } from "vuelidate/lib/validators";
import VueTypes from "vue-types";
import { Api, apiRequest } from "@/api";
import { Sources } from "@/components";
import { INPUTS, FIELD_INPUTS, FIELD_SELECT } from "@/entities/fields";
import { FIELD_VALIDATORS_TYPES } from "./utils";
import Validators from "./Validators.vue";
import Options from "./Options.vue";
import Dictionaries from "./Dictionaries.vue";

export const ACTIONS = {
	CREATE: "CREATE",
	UPDATE: "CREATE"
};

export default {
	name: "EntityForm",
	components: {
		Validators,
		Sources,
		Options,
		Dictionaries
	},
	mixins: [validationMixin],
	props: {
		entityKey: VueTypes.string.required,
		node: VueTypes.object.required,
		selectGroups: VueTypes.any.required,
		action: VueTypes.string.def(ACTIONS.CREATE)
	},
	data() {
		return {
			key: "",
			name: "",
			description: "",
			groupKey: "",
			defaultExpression: (this.node || {}).default || "",
			sampleDataId: null,
			dataTrialResult: {},
			validators: {},
			mappers: {},
			styles: [],
			priority: 4,
			type: FIELD_SELECT[0].key,
			dictionary: "",
			multiple: true,
			hidden: true,
			locked: false,
			visible: true,
			editable: true,
			...this.node,

			types: FIELD_SELECT,
			isLoading: false,
			priorities: [
				{ key: 4, label: "No priority" },
				{ key: 1, label: "Priority 1" },
				{ key: 2, label: "Priority 2" },
				{ key: 3, label: "Priority 3" }
			]
		};
	},
	validations() {
		return {
			key: { required },
			name: { required },
			priority: { required }
		};
	},
	computed: {
		normalizeGroups() {
			const selectGroup = (this.selectGroups || {}).groups || {};
			return Object.keys(selectGroup).map(key => ({ key, label: selectGroup[key] }));
		},
		isSelect() {
			return FIELD_INPUTS[this.type] === INPUTS.SELECT || FIELD_INPUTS[this.type] === INPUTS.SELECTSINGLE;
		},
		compilingExpression() {
			return this.dataTrialResult === "waiting";
		},
		compileDisabled() {
			return this.compilingExpression || !this.sampleDataId || !this.defaultExpression;
		}
	},
	watch: {
		type: {
			handler(type) {
				if (FIELD_INPUTS[type] !== INPUTS.SELECT && FIELD_INPUTS[type] !== INPUTS.SELECTSINGLE) {
					this.dictionary = "";
					this.multiple = true;
				}
				this.validators = this.normalizeValidators({ validatorType: FIELD_INPUTS[type], validators: this.validators });
			},
			immediate: true
		},
		editable: {
			/**
			 * Si el valor de "editable" se pone en "false", se pone el campo "Prioridad" sin prioridad.
			 * @param {boolean} editable Valor del campo editable
			 */
			handler(newValue) {
				if (!newValue) {
					this.priority = 4;
				}
			}
		}
	},
	methods: {
		normalizeValidators({ validatorType, validators = {} }) {
			return (FIELD_VALIDATORS_TYPES[validatorType] || []).reduce((acc, { key, type, defaultValue }) => {
				if (!validators[key]) return { ...acc, [key]: typeof defaultValue !== "undefined" ? defaultValue : false };
				return { ...acc, [key]: [INPUTS.DATE].includes(type) ? new Date(validators[key]) : validators[key] };
			}, {});
		},
		async handleSubmit() {
			this.$v.$touch();

			if (!this.$v.$invalid) {
				this.isLoading = true;
				const { key = "" } = this.node || {};
				const params = {
					key: this.key,
					name: this.name,
					description: this.description,
					groupKey: this.groupKey,
					type: this.type,
					dictionary: this.dictionary,
					multiple: this.multiple,
					default: this.defaultExpression,
					validators: this.validators,
					styles: this.styles,
					mappers: this.mappers,
					hidden: !this.visible,
					editable: this.editable,
					priority: this.priority
				};

				const method = ACTIONS.CREATE === this.action ? "create" : "update";
				apiRequest(Api().entities[method](this.entityKey, params, key))
					.then(data => this.$emit("onSubmit", data))
					.catch(() => this.$emit("onError"))
					.finally(() => (this.isLoading = false));
			}
		},
		compileExpression() {
			this.dataTrialResult = "waiting";
			const sampleId = parseInt(this.sampleDataId, 10);
			const params = { sampleId, expression: this.defaultExpression };
			apiRequest(Api().entities.compileExpression(this.entityKey, params))
				.then(({ data }) => {
					this.dataTrialResult = {
						result: data.result,
						error: this.$t("entities.creation.noData")
					};
				})
				.catch(({ result }) => {
					this.dataTrialResult = {
						error: result.data.message,
						result: this.$t("entities.creation.noData")
					};
				});
		}
	}
};
</script>
<style lang="scss" scoped>
.WText.Title {
	display: block;
	margin-bottom: 15px !important;
	padding: 5px;
	background: $gray-06;
	border-radius: $border-radius;
}

.Options {
	.Item {
		padding: 8px 0;

		& + .Item {
			border-top: solid 1px $gray-05;
		}
	}
}
</style>
